import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { Control, DivIcon, FeatureGroup, GeoJSON, LatLng, latLng, LatLngBounds, LayerGroup, Map, MapOptions, Marker, MarkerOptions, Path, tileLayer, TileLayer } from 'leaflet';
import proj4 from 'proj4';
import { Subject } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { Organisation } from '../../interfaces/routering/organisation';
import { DEFAULT_CITY_BORDERS_GEOJSON_STYLE, DEFAULT_GRID_BORDERS_GEOJSON_STYLE, GeoJsonStyleOptions } from '../../../@fury/utils/leaflet';
import { HttpClient } from '@angular/common/http';
import { OverviewService } from '../overview/overview.service';
import { centroid } from '@turf/turf';
import Layers = Control.Layers;
import Scale = Control.Scale;

@Injectable()

export class LeafletMapService {

  public mapLayerStandard: TileLayer = tileLayer('https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png', {
    detectRetina: true,
    attribution: 'Kaartgegevens &copy; <a href="https://www.kadaster.nl">Kadaster</a>',
    minZoom: 6,
    maxZoom: 19,
  });

  public mapLayerSatellite: TileLayer = tileLayer('https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.png', {
    detectRetina: true,
    attribution: 'Kaartgegevens &copy; <a href="https://www.kadaster.nl">Kadaster</a>',
    minZoom: 6,
    maxZoom: 19,
  });

  public zoom: number = 12;
  public center: LatLng = latLng(52.08486, 4.26783);

  public mapOptions: MapOptions = {
    layers: [this.mapLayerStandard],
    zoom: this.zoom,
    center: this.center
  };

  public baseMaps = {
    'Achtergrond kaart': this.mapLayerStandard,
    'Luchtfoto kaart': this.mapLayerSatellite
  };

  // noinspection JSUnusedGlobalSymbols
  private toggleControl = L.Control.extend({
    onAdd: (): HTMLDivElement => {
      const container: HTMLDivElement = L.DomUtil.create('div', 'layer-toggle leaflet-bar');

      const cityBtn: HTMLAnchorElement = L.DomUtil.create('a', 'layer-toggle-btn', container) as HTMLAnchorElement;
      cityBtn.href = '#';
      cityBtn.title = 'Toggle City Borders';
      cityBtn.innerHTML = '<span class="material-icons-outlined">flag</span>';

      const gridBtn: HTMLAnchorElement = L.DomUtil.create('a', 'layer-toggle-btn', container) as HTMLAnchorElement;
      gridBtn.href = '#';
      gridBtn.title = 'Toggle Grid';
      gridBtn.innerHTML = '<span class="material-icons-outlined">border_all</span>';

      const labelBtn: HTMLAnchorElement = L.DomUtil.create('a', 'layer-toggle-btn', container) as HTMLAnchorElement;
      labelBtn.href = '#';
      labelBtn.title = 'Toggle Labels';
      labelBtn.innerHTML = '<span class="material-icons-outlined">label</span>';

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      const setActive = (el: HTMLElement, active: boolean): void => {
        el.classList.toggle('is-active', active);
      };

      const toggleLayer = (layer?: L.Layer, btn?: HTMLElement): void => {
        if (!this.mapContainer || !layer || !btn) {
          return;
        }

        const isOn: boolean = this.mapContainer.hasLayer(layer);
        if (isOn) {
          this.mapContainer.removeLayer(layer);
        } else {
          this.mapContainer.addLayer(layer);
        }

        setActive(btn, !isOn);
      };


      setActive(cityBtn, true);
      setActive(gridBtn, true);
//        setActive(labelBtn, true);

      L.DomEvent.on(cityBtn, 'click', (e: Event): void => {
        L.DomEvent.preventDefault(e);
        toggleLayer(this.borderLayer, cityBtn);
      });

      L.DomEvent.on(gridBtn, 'click', (e: Event): void => {
        L.DomEvent.preventDefault(e);
        toggleLayer(this.gridLayer, gridBtn);
      });

      L.DomEvent.on(labelBtn, 'click', (e: Event): void => {
        L.DomEvent.preventDefault(e);

        toggleLayer(this.gridLabelLayer, labelBtn);
      });

      return container;
    },
  });

  public markerLayer: FeatureGroup = new FeatureGroup();
  public mapContainer: Map = null;
  private borderPane = null;
  private borderLayer: GeoJSON = null;
  private gridLayer: GeoJSON = null;
  private gridLabelLayer: LayerGroup = new LayerGroup();
  private gridPane = null;
  public singleMarker: Marker = null;

  private defaultZoomed: number = 17;

  public zoomChanged$: Subject<number> = new Subject<number>();

  constructor(
    private http: HttpClient,
    private overviewService: OverviewService,
    private authenticationService: AuthenticationService,
  ) {
  }

  init(mapContainer: Map): void {
    this.mapContainer = mapContainer;

    if (mapContainer !== null) {
      this.addControls();
      this.addLayers();


      this.authenticationService.organisation$.subscribe((organisation: Organisation): void => {
        if (organisation.center_lat && organisation.center_lng && organisation.center_zoom) {
          setTimeout((): void => {
            if (this.mapContainer !== null) {
              this.mapContainer.setView([organisation.center_lat, organisation.center_lng], organisation.center_zoom);
            }
          }, 100);
          this.zoom = organisation.center_zoom;
          this.center = latLng(organisation.center_lat, organisation.center_lng);
        }
      });

      // on the initial app load with map view open, a map centre is off, reset it
      setTimeout((): void => {
        if (this.mapContainer !== null) {
          this.mapContainer.invalidateSize();
        }
      }, 100);
    }

    this.mapContainer.on('zoomend', (): void => this.zoomChanged$.next(this.mapContainer.getZoom()));
  }

  private addControls(): void {
    this.mapContainer.addControl(new this.toggleControl({position: 'topleft'}));
    this.mapContainer.addControl(new Layers(this.baseMaps));
    this.mapContainer.addControl(new Scale({maxWidth: 200, metric: true, imperial: false, updateWhenIdle: true, position: 'bottomright'}));
  }

  private addLayers(): void {
    // add layer for grid tooltips
    this.gridLabelLayer.addTo(this.mapContainer);

    // add layer for city borders
    this.loadCityBordersGeoJson('assets/geojsons/cityborders.geojson');

    // add layer for grid
    this.overviewService.tiles().subscribe((response: any): void => this.addGeoJsonGridLayer(response, DEFAULT_GRID_BORDERS_GEOJSON_STYLE));

    // add layer for marker
    this.markerLayer.addTo(this.mapContainer);
  }

  private loadCityBordersGeoJson(url: string): void {
    this.http.get<any>(url).subscribe({
      next: (geojson) => this.mapContainer.whenReady(() => this.addGeoJsonBorderLayer(geojson, DEFAULT_CITY_BORDERS_GEOJSON_STYLE)),
      error: (err): void => console.error('Failed to load GeoJSON:', err),
    });
  }

  private addGeoJsonBorderLayer(geojson: any, styleOptions: GeoJsonStyleOptions): void {
    if (!this.mapContainer) {
      return;
    }

    if (this.borderLayer) {
      this.borderLayer.removeFrom(this.mapContainer);
    }

    if (!this.borderPane) {
      this.borderPane = this.mapContainer.createPane('borderPane').style.zIndex = '610';
    }

    const normalStyle: L.PathOptions = {
      color: styleOptions.strokeColor,
      fillColor: styleOptions.fillColor,
      weight: styleOptions.weight,
      opacity: styleOptions.opacity,
      fillOpacity: styleOptions.fillOpacity,
    };

    this.borderLayer = L.geoJSON(geojson, {
      pane: 'borderPane',
      style: () => normalStyle,
    }).addTo(this.mapContainer);


    const bounds: LatLngBounds = this.borderLayer.getBounds();
    if (bounds.isValid()) {
      this.mapContainer.fitBounds(bounds, {padding: [20, 20]});
    }
  }

  private addGeoJsonGridLayer(geojson: any, styleOptions: GeoJsonStyleOptions): void {
    if (!this.mapContainer) {
      return;
    }

    if (!geojson) {
      return;
    }

    if (!this.gridPane) {
      this.gridPane = this.mapContainer.createPane('gridPane').style.zIndex = '620';
    }

    if (this.gridLabelLayer) {
      this.gridLabelLayer.clearLayers();
      this.gridLabelLayer.removeFrom(this.mapContainer);
    }

    if (this.gridLayer) {
      this.gridLayer.clearLayers();
      this.gridLayer.removeFrom(this.mapContainer);
    }

    const normalStyle: L.PathOptions = {
      color: styleOptions.strokeColor,
      fillColor: styleOptions.fillColor,
      weight: styleOptions.weight,
      opacity: styleOptions.opacity,
      fillOpacity: styleOptions.fillOpacity,
    };

    this.gridLayer = L.geoJSON(geojson, {
      pane: 'gridPane',
      style: () => normalStyle,
      onEachFeature: (feature, layer): void => {
        const label: string = feature?.properties?.label ?? feature?.properties?.label;
        if (label) {
          const centerFeature = centroid(feature);
          const [lng, lat] = centerFeature.geometry.coordinates;

          const marker = L.marker([lat, lng], {
            interactive: false,
            opacity: 0,
          });

          marker.bindTooltip((): string => {
              return String(label);
            },
            {
              permanent: true,
              direction: 'center',
              className: 'grid-label',
              opacity: 0.95,
              offset: [-15, 28]
            });

          marker.addTo(this.gridLabelLayer);
        }
      }
    }).addTo(this.mapContainer);

    this.mapContainer.removeLayer(this.gridLabelLayer);
  }

  marker(iconColor: string = null): MarkerOptions {
    return {
      icon: new DivIcon({
        html: '<div class="leaflet-custom-marker-pin"' + (iconColor ? 'style="color:' + iconColor + '"' : '') + '></div>',
        iconSize: [24, 36],
        iconAnchor: [12, 36],
      }),
      draggable: false,
    };
  }

  updateMarker(coords: [number, number], zoom: number = null): void {
    if (this.singleMarker) {
      this.singleMarker.setLatLng({lat: coords[0], lng: coords[1]});
    } else {
      this.singleMarker = new Marker({lat: coords[0], lng: coords[1]}, this.marker('single'));
      this.markerLayer.addLayer(this.singleMarker);
    }

    if (this.mapContainer !== null && this.mapContainer !== undefined) {
      this.mapContainer.setView(coords, zoom ?? this.defaultZoomed);
    }
  }

  clear(clearContainer: boolean = false): void {
    this.markerLayer.clearLayers();
    this.singleMarker = null;
    if (document.querySelector('.legend-toggle') !== null) {
      document.querySelector('.legend-toggle').removeAllListeners('click');
    }

    if (clearContainer && this.mapContainer !== null) {
      this.mapContainer = null;
    }
  }

  convertLatLngToNewRd(coords: [number, number]): [number, number] {
    proj4.defs('EPSG:28992',
      '+proj=sterea +lat_0=52.1561605555556 +lon_0=5.38763888888889 ' +
      '+k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel ' +
      '+towgs84=565.4171,50.3319,465.5524,-0.398957,0.343988,-1.8774,4.0725 ' +
      '+units=m +no_defs +type=crs'
    );
    return proj4('EPSG:4326', 'EPSG:28992', coords);
  }

  convertNewRdToLatLng(coords: [number, number]): [number, number] {
    console.log(coords);
    proj4.defs('EPSG:28992',
      '+proj=sterea +lat_0=52.1561605555556 +lon_0=5.38763888888889 ' +
      '+k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel ' +
      '+towgs84=565.4171,50.3319,465.5524,-0.398957,0.343988,-1.8774,4.0725 ' +
      '+units=m +no_defs +type=crs'
    );
    return proj4('EPSG:28992', 'EPSG:4326', coords);
  }
}
